<template>
  <page class="full-height">
    <div>
      <div
        class="flex flex-wrap items-end justify-between page-header-content gap-4 pb-5 md:items-start"
      >
        <div class="flex-col align-items-center flex-wrap">
          <h1 class="schedule-board-title page-header-1 !mb-0.5 tracking-tight">
            Schedule
          </h1>
          <p class="schedule-board-date text-base !text-gray-500">
            {{ title }}
          </p>
        </div>
        <div
          data-cy="schedule-board-actions"
          class="flex flex-wrap items-center gap-2 md:gap-4"
        >
          <!-- Desktop renders the toggle in the filter bar; on mobile the
               filters collapse into a modal, so surface it in the header. -->
          <ScheduleViewToggle class="inline-flex md:hidden" />
          <AppointmentColorsLegend />
          <div class="hidden md:flex">
            <SlotWrapper>
              <template #actions>
                <schedule-board-filters
                  class="schedule-board-filters"
                ></schedule-board-filters>
              </template>
            </SlotWrapper>
          </div>
          <div class="md:hidden">
            <button
              type="button"
              class="text-black bg-white border hover:bg-blue-800 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-lg p-2.5 text-center inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              @click="toggleFilterModal"
            >
              <i class="fa-regular fa-filter"></i>
            </button>
          </div>
          <CheckAvailabilityModal class="md:hidden" />
        </div>
        <Modal
          v-if="filterModal"
          title="Filter"
          @hide-modal="toggleFilterModal"
          @submit="toggleFilterModal"
        >
          <div>
            <SlotWrapper>
              <template #actions>
                <schedule-board-filters
                  class="schedule-board-filters"
                ></schedule-board-filters>
              </template>
            </SlotWrapper>
          </div>
        </Modal>
      </div>
      <schedule-board class="schedule-board mt-4"></schedule-board>
      <AppointmentDetailsDrawer
        v-if="drawerOpen"
        :appointment-id="selectedAppointmentId"
        @hide-drawer="handleDrawerClosed"
        @appointment-updated="handleAppointmentUpdated"
      />
      <div class="fixed bottom-12 right-4 z-10 flex gap-3 md:hidden">
        <CreateNewFOB :default-date="scheduleBoardStart" />
      </div>
    </div>
  </page>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { reportRumError, trackRumAction } from "@/lib/datadog.js";
import { EventBus } from "@/lib/event_bus.js";
import { syncStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";
import SlotWrapper from "@/components/SlotWrapper";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import VueChannel from "@/channels/VueChannel";
import { useTeardownStrandGuard } from "@/composables/useTeardownStrandGuard";
import AppointmentColorsLegend from "@/Scheduler/AppointmentColorsLegend.vue";
import CreateNewFOB from "@/SchedulerV2/CreateNewFOB.vue";
import CheckAvailabilityModal from "./CheckAvailabilityModal.vue";
import ScheduleBoard from "./ScheduleBoard.vue";
import { scheduleBoardDateLabel } from "./scheduleBoardDateLabel";
import ScheduleBoardFilters from "./ScheduleBoardFilters.vue";
import ScheduleViewToggle from "./ScheduleViewToggle.vue";

const store = useStore();
const filterModal = ref(false);
const selectedAppointmentId = ref<string | number | null>(null);
let channel: VueChannel | null = null;
const scheduleBoardStart = computed<Date | string>(
  () => store.state.scheduleBoardFilters.start
);
const title = computed(() =>
  scheduleBoardDateLabel(
    scheduleBoardStart.value,
    store.getters.scheduleWeekViewActive
  )
);
const drawerOpen = computed(() => Boolean(selectedAppointmentId.value));

// Observability (TOF-617): flag when closing the appointment drawer leaves it in
// the DOM (teardown did not complete). backdrop_present tells us whether the
// sibling in-tree backdrop was removed while the teleported drawer stranded.
useTeardownStrandGuard(
  drawerOpen,
  () => document.querySelector(".drawer.grid"),
  "schedule-appointment-drawer",
  {
    countSelector: ".drawer.grid",
    diagnostics: () => ({
      // The manual in-tree backdrop only — exclude any Flowbite backdrop, which
      // carries the same fixed/inset-0/z-50 classes but a drawer-backdrop attr.
      backdrop_present: Boolean(
        document.querySelector(".fixed.inset-0.z-50:not([drawer-backdrop])")
      )
    })
  }
);

const handleDrawerOpen = (appointmentId: string | number) => {
  selectedAppointmentId.value = appointmentId;
  // The drawer stays mounted across appointment switches (no :key remount), so
  // sync the URL here rather than relying on the drawer's one-time show event.
  updateQueryParam("appointment_id", appointmentId.toString());
};
// "View Appointment" from the create toast: navigate the board to the new
// appointment's date (so its bar is actually on screen — week view is often
// on a different week) and then open its drawer.
const handleSchedulerEventView = (
  appointmentId: string | number,
  isoDate?: string
) => {
  const date = isoDate ? new Date(isoDate) : null;
  // Only navigate for a valid date — never feed Invalid Date to the store/gotoDate
  if (date && !Number.isNaN(date.getTime())) {
    store.dispatch("setScheduleBoardFilters", { start: date });
    syncStartParam(date, { replace: true });
    // Observability (ENG-241): same global-handle nav as the day arrows; record
    // handle presence and capture any throw. Additive — rethrow preserves behavior.
    const fcPresent = Boolean(window._FC);
    try {
      window._FC?.gotoDate(date);
      trackRumAction("schedule_day_nav", {
        source: "event_view",
        requested: date.toISOString(),
        fc_present: fcPresent
      });
    } catch (error) {
      reportRumError("schedule_day_nav_gotodate_threw", {
        source: "event_view",
        requested: date.toISOString(),
        fc_present: fcPresent,
        thrown: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  handleDrawerOpen(appointmentId);
};
const handleDrawerClosed = () => {
  selectedAppointmentId.value = null;
  updateQueryParam("appointment_id", null);
};
const handleAppointmentUpdated = () => {
  EventBus.$emit("refresh-schedule-board");
};

onMounted(() => {
  // Fire-and-forget: the board must render whether or not this resolves.
  import("./scheduler_tailwind.css");

  store.dispatch("fetchDepartments");
  EventBus.$on("open-appointment-drawer", handleDrawerOpen);
  EventBus.$on("appointment-updated", handleAppointmentUpdated);
  const { appointment_id } = getQueryVariables();
  if (appointment_id) {
    handleDrawerOpen(appointment_id);
  }
  window.handleSchedulerEventEdit = handleDrawerOpen;
  window.handleSchedulerEventView = handleSchedulerEventView;

  channel = new VueChannel();
  cable.subscribe(channel).catch((err) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
});

onBeforeUnmount(() => {
  EventBus.$off("open-appointment-drawer", handleDrawerOpen);
  EventBus.$off("appointment-updated", handleAppointmentUpdated);
  channel?.disconnect();
  window.handleSchedulerEventEdit = null;
  window.handleSchedulerEventView = null;
});

const toggleFilterModal = () => (filterModal.value = !filterModal.value);
</script>

<style lang="scss" scoped>
.schedule-board-filters {
  z-index: 2;
}
.schedule-board {
  z-index: 1;
  position: sticky;
  top: 0;
}

:deep(.row) {
  height: 100%;
}
.schedule-board-date {
  font-weight: 400 !important;
  // Week-range labels read as one breath: keep the en dash from wrapping
  white-space: nowrap;
}
</style>
